import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaUpload, FaPlay, FaPause, FaDownload, FaCrop, FaPalette, FaMagic, FaArrowLeft } from 'react-icons/fa';

const WormieEdit = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState(null);
  const [isPreviewingTrim, setIsPreviewingTrim] = useState(false);
  const [autoPreviewTimeout, setAutoPreviewTimeout] = useState(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [boomerangProgress, setBoomerangProgress] = useState(0);
  const [boomerangDirection, setBoomerangDirection] = useState('forward'); // 'forward' or 'reverse'
  const boomerangRunningRef = useRef(false); // Controls the boomerang loop
  const [isPlaying, setIsPlaying] = useState(false);
  const [smoothingPoints, setSmoothingPoints] = useState([]);
  const [invisibleLoopEnabled, setInvisibleLoopEnabled] = useState(true);
  const [boomerangFrames, setBoomerangFrames] = useState([]);
  const [processingProgress, setProcessingProgress] = useState(0);
  
  const videoRef = useRef(null);
  const effectsVideoRef = useRef(null); // Separate ref for step 3 video
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const timelineTrackRef = useRef(null);
  const timelineSelectionRef = useRef(null);
  const startHandleRef = useRef(null);
  const endHandleRef = useRef(null);
  const playheadRef = useRef(null);

  // Step navigation
  const goToStep = (step) => {
    // Stop any playing boomerang when leaving step 3
    if (currentStep === 3 && step !== 3 && isPlaying) {
      setIsPlaying(false);
      if (window.stopBoomerangPlayback) {
        window.stopBoomerangPlayback();
      }
    }
    setCurrentStep(step);
  };

  // File upload handling
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
      setIsUploadingVideo(true);
      const videoUrl = URL.createObjectURL(file);
      
      // Create a temporary video element to get metadata
      const tempVideo = document.createElement('video');
      tempVideo.src = videoUrl;
      tempVideo.muted = true;
      
      tempVideo.onloadedmetadata = () => {
        const duration = tempVideo.duration;
        setVideoDuration(duration);
        setTrimStart(0);
        setTrimEnd(duration);
        setIsUploadingVideo(false);
        
        // Auto-advance to trim step
        goToStep(2);
        
        // Set up the main video ref when we get to step 2
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.src = videoUrl;
            videoRef.current.currentTime = 0;
            videoRef.current.muted = true;
            videoRef.current.loop = true;
            
            // Set up timeline controls after video loads
            videoRef.current.onloadeddata = () => {
              console.log('✅ Main video loaded and ready for playback');
              setupTrimControls();
              // Don't auto-start preview since user said they don't want it
              setIsPlaying(false); // Ensure we start in paused state
            };
            
            // Handle video load errors
            videoRef.current.onerror = (e) => {
              console.error('Video load error:', e);
              alert('Error loading video for playback. Please try again.');
            };
          }
        }, 100);
      };
      
      tempVideo.onerror = () => {
        setIsUploadingVideo(false);
        alert('Error loading video. Please try a different file.');
        setSelectedFile(null);
      };
    }
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!videoRef.current) {
      console.warn('⚠️ Video ref not available');
      return;
    }

    const video = videoRef.current;
    
    if (video.paused) {
      console.log('▶️ Playing video');
      video.play().then(() => {
        console.log('✅ Video started playing');
        setIsPlaying(true);
      }).catch(e => {
        console.error('❌ Play failed:', e);
        setIsPlaying(false);
        // Try to provide helpful error message
        if (e.name === 'NotAllowedError') {
          alert('Video playback was blocked. Click the video or this button again to play.');
        }
      });
    } else {
      console.log('⏸️ Pausing video');
      video.pause();
      setIsPlaying(false);
    }
  };

  // Keep playing state in sync with video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      console.log('🎵 Video play event');
      setIsPlaying(true);
    };
    const handlePause = () => {
      console.log('⏸️ Video pause event');
      setIsPlaying(false);
    };
    const handleEnded = () => {
      console.log('🏁 Video ended event');
      setIsPlaying(false);
    };
    const handleLoadedData = () => {
      console.log('📹 Video loaded data, current state:', video.paused ? 'paused' : 'playing');
      setIsPlaying(!video.paused);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('loadeddata', handleLoadedData);

    // Set initial state based on video's current state
    setIsPlaying(!video.paused);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [selectedFile]);

  // Keyboard controls for trim step
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentStep !== 2 || !videoRef.current) return;
      
      switch(e.key) {
        case ' ':
          e.preventDefault();
          if (isPreviewingTrim) {
            stopBoomerangPreview();
          } else {
            startBoomerangPreview();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          videoRef.current.currentTime = Math.max(trimStart, videoRef.current.currentTime - (1/30));
          break;
        case 'ArrowRight':
          e.preventDefault();
          videoRef.current.currentTime = Math.min(trimEnd, videoRef.current.currentTime + (1/30));
          break;
        case 'Home':
          e.preventDefault();
          videoRef.current.currentTime = trimStart;
          break;
        case 'End':
          e.preventDefault();
          videoRef.current.currentTime = trimEnd;
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, trimStart, trimEnd, isPreviewingTrim]);

  // Timeline controls setup
  const setupTrimControls = () => {
    if (!videoRef.current) return;
    
    // Update playhead position on timeupdate
    videoRef.current.ontimeupdate = updatePlayheadPosition;
  };

  // Handle dragging
  const startDrag = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragHandle(handle);
    
    // Pause video during drag for better scrubbing
    if (videoRef.current) {
      videoRef.current.pause();
    }
    
    // Change cursor during drag
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    
    const onDrag = (e) => {
      if (!timelineTrackRef.current || !videoRef.current || !videoDuration) return;
      
      const rect = timelineTrackRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const time = percentage * videoDuration;
      
      if (handle === 'start') {
        const newStart = Math.min(time, trimEnd - 0.1);
        setTrimStart(newStart);
        // Immediate frame update during drag
        videoRef.current.currentTime = newStart;
      } else if (handle === 'end') {
        const newEnd = Math.max(time, trimStart + 0.1);
        setTrimEnd(newEnd);
        // Immediate frame update during drag
        videoRef.current.currentTime = newEnd;
      }
      
      // Force playhead update immediately
      updatePlayheadPosition();
    };
    
    const stopDrag = () => {
      setIsDragging(false);
      setDragHandle(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
      
      // Schedule auto-preview after dragging stops
      scheduleAutoPreview();
    };
    
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', stopDrag);
  };

  // Handle timeline click to seek
  const handleTimelineClick = (e) => {
    if (isDragging || !timelineTrackRef.current || !videoRef.current) return;
    
    const rect = timelineTrackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const time = percentage * videoDuration;
    
    // Pause and seek for immediate scrubbing feedback
    videoRef.current.pause();
    videoRef.current.currentTime = time;
  };

  // Add timeline scrubbing (click and drag anywhere on timeline)
  const startTimelineScrub = (e) => {
    if (isDragging || !timelineTrackRef.current || !videoRef.current) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragHandle('scrub');
    
    // Pause video for scrubbing
    videoRef.current.pause();
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    
    const onScrub = (e) => {
      if (!timelineTrackRef.current || !videoRef.current || !videoDuration) return;
      
      const rect = timelineTrackRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const time = percentage * videoDuration;
      
      // Update video time immediately for real-time scrubbing
      videoRef.current.currentTime = time;
      updatePlayheadPosition();
    };
    
    const stopScrub = () => {
      setIsDragging(false);
      setDragHandle(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onScrub);
      document.removeEventListener('mouseup', stopScrub);
      
      // Schedule auto-preview after scrubbing
      scheduleAutoPreview();
    };
    
    // Initial scrub to current position
    onScrub(e);
    
    document.addEventListener('mousemove', onScrub);
    document.addEventListener('mouseup', stopScrub);
  };

  // Update timeline visual elements - now using React state for positioning
  useEffect(() => {
    updateTimelineVisual();
  }, [trimStart, trimEnd, videoDuration]);

  const updateTimelineVisual = () => {
    // This will be handled by React state in the JSX now
  };

  // Update playhead position
  const updatePlayheadPosition = () => {
    if (!playheadRef.current || !videoRef.current || !videoDuration) return;
    
    const currentPercentage = (videoRef.current.currentTime / videoDuration) * 100;
    playheadRef.current.style.left = `${currentPercentage}%`;
  };

  // Auto-preview functionality
  const scheduleAutoPreview = () => {
    // Don't auto-preview if already previewing
    if (isPreviewingTrim) return;
    
    if (autoPreviewTimeout) {
      clearTimeout(autoPreviewTimeout);
    }
    
    const timeout = setTimeout(() => {
      // Double-check we're still not previewing and step is correct
      if (!isPreviewingTrim && currentStep === 2) {
        console.log('Auto-starting boomerang preview after drag');
        startBoomerangPreview(); // Use boomerang preview for auto-preview
      }
    }, 1500); // Longer delay for auto-preview
    setAutoPreviewTimeout(timeout);
  };

  const startBoomerangPreview = async () => {
    console.log('🎬 === BOOMERANG PREVIEW START ===');
    console.log('Checks:');
    console.log('  currentStep === 2?', currentStep === 2);
    console.log('  videoRef.current exists?', !!videoRef.current);
    console.log('  isPreviewingTrim?', isPreviewingTrim);
    
    if (currentStep !== 2) {
      console.log('❌ STOPPED: Wrong step (need step 2, got', currentStep, ')');
      return;
    }
    
    if (!videoRef.current) {
      console.log('❌ STOPPED: No video ref');
      return;
    }
    
    if (isPreviewingTrim) {
      console.log('❌ STOPPED: Already previewing');
      return;
    }
    
    const duration = trimEnd - trimStart;
    console.log('📏 Duration check:', duration, 'seconds');
    
    if (duration < 0.1 || duration > 30) {
      console.log('❌ STOPPED: Duration out of range (0.1-30s required)');
      alert(`Clip duration ${duration.toFixed(1)}s is ${duration < 0.1 ? 'too short' : 'too long'} for preview`);
      return;
    }
    
    console.log('✅ All checks passed, starting preview');
    console.log('🎬 Starting visual boomerang preview for', duration.toFixed(2), 'second clip');
    
    console.log('🔄 Setting state: isPreviewingTrim = true');
    setIsPreviewingTrim(true);
    console.log('🔄 Setting state: boomerangProgress = 0');
    setBoomerangProgress(0);
    console.log('🔄 Setting state: boomerangDirection = forward');
    setBoomerangDirection('forward');
    console.log('🔄 Setting ref: boomerangRunningRef = true');
    boomerangRunningRef.current = true;
    
    try {
      const video = videoRef.current;
      console.log('📹 Video element ready, pausing...');
      video.pause(); // Ensure we start paused for frame control
      console.log('📹 Video paused, current time:', video.currentTime);
      
      // Visual boomerang with progress overlay
      const loops = Math.max(2, Math.min(4, Math.ceil(6 / duration))); // 2-4 loops
      console.log(`🔄 Playing ${loops} visual boomerang loops`);
      
      console.log('🔍 About to start loop:');
      console.log('  loops:', loops);
      console.log('  boomerangRunningRef.current:', boomerangRunningRef.current);
      console.log('  isPreviewingTrim (async state):', isPreviewingTrim);
      
      console.log('🎯 Entering for loop with condition: loop < loops && boomerangRunningRef.current');
      console.log('  Initial values: loop=0, loops=' + loops + ', boomerangRunningRef.current=' + boomerangRunningRef.current);
      
      for (let loop = 0; loop < loops && boomerangRunningRef.current; loop++) {
        console.log(`🎬 === LOOP ${loop + 1}/${loops} START ===`);
        console.log('  Loop iteration values: loop=' + loop + ', loops=' + loops + ', boomerangRunningRef.current=' + boomerangRunningRef.current);
        
        // FORWARD PASS - Show progress moving forward
        console.log('▶️ FORWARD PASS START');
        console.log('🔄 Setting direction: forward');
        setBoomerangDirection('forward');
        const forwardFrames = Math.max(15, Math.min(40, Math.ceil(duration * 20))); // 15-40 frames
        console.log('📊 Forward frames calculated:', forwardFrames);
        
        for (let i = 0; i <= forwardFrames && boomerangRunningRef.current; i++) {
          const progress = i / forwardFrames;
          const currentTime = trimStart + (progress * duration);
          
          if (i % 5 === 0) { // Log every 5th frame to avoid spam
            console.log(`▶️ Forward frame ${i}/${forwardFrames}, progress: ${(progress * 100).toFixed(1)}%, time: ${currentTime.toFixed(2)}`);
          }
          
          video.currentTime = currentTime;
          setBoomerangProgress(progress * 100); // 0-100%
          
          await new Promise(resolve => setTimeout(resolve, 60)); // 60ms = ~16fps
        }
        
        console.log('▶️ FORWARD PASS COMPLETE');
        
        // Small pause at end
        console.log('⏸️ Small pause at end of forward pass...');
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // REVERSE PASS - Show progress moving backward
        console.log('◀️ REVERSE PASS START');
        console.log('🔄 Setting direction: reverse');
        setBoomerangDirection('reverse');
        const reverseFrames = Math.max(12, Math.min(30, Math.ceil(duration * 15))); // Slightly faster reverse
        console.log('📊 Reverse frames calculated:', reverseFrames);
        
        for (let i = reverseFrames; i >= 0 && boomerangRunningRef.current; i--) {
          const progress = i / reverseFrames;
          const currentTime = trimStart + (progress * duration);
          
          if (i % 5 === 0) { // Log every 5th frame to avoid spam
            console.log(`◀️ Reverse frame ${i}/${reverseFrames}, progress: ${(progress * 100).toFixed(1)}%, time: ${currentTime.toFixed(2)}`);
          }
          
          video.currentTime = currentTime;
          setBoomerangProgress(progress * 100); // 100-0%
          
          await new Promise(resolve => setTimeout(resolve, 45)); // 45ms = ~22fps (faster reverse)
        }
        
        console.log('◀️ REVERSE PASS COMPLETE');
        
        // Pause between loops
        console.log('⏸️ Pause between loops...');
        await new Promise(resolve => setTimeout(resolve, 300));
        console.log(`🎬 === LOOP ${loop + 1}/${loops} END ===`);
      }
      
      // Return to start position
      console.log('🏁 All loops complete, returning to start position');
      if (boomerangRunningRef.current) {
        console.log('📍 Setting video to trimStart:', trimStart);
        video.currentTime = trimStart;
        console.log('📊 Resetting progress to 0');
        setBoomerangProgress(0);
      }
      
      console.log('✅ Visual boomerang preview completed successfully');
    } catch (error) {
      console.error('❌ Boomerang preview error:', error);
      console.error('Error stack:', error.stack);
    } finally {
      console.log('🧹 Cleanup: Setting boomerangRunningRef = false');
      boomerangRunningRef.current = false;
      console.log('🧹 Cleanup: Setting isPreviewingTrim = false');
      setIsPreviewingTrim(false);
      console.log('🧹 Cleanup: Setting boomerangProgress = 0');
      setBoomerangProgress(0);
      console.log('🧹 Cleanup: Setting boomerangDirection = forward');
      setBoomerangDirection('forward');
      console.log('🎬 === BOOMERANG PREVIEW END ===');
    }
  };

  const stopBoomerangPreview = () => {
    console.log('⏹️ === STOPPING BOOMERANG PREVIEW ===');
    console.log('Current state before stop:');
    console.log('  isPreviewingTrim:', isPreviewingTrim);
    console.log('  boomerangProgress:', boomerangProgress);
    console.log('  boomerangDirection:', boomerangDirection);
    
    console.log('🔄 Setting ref: boomerangRunningRef = false');
    boomerangRunningRef.current = false;
    console.log('🔄 Setting isPreviewingTrim = false');
    setIsPreviewingTrim(false);
    console.log('🔄 Setting boomerangProgress = 0');
    setBoomerangProgress(0);
    console.log('🔄 Setting boomerangDirection = forward');
    setBoomerangDirection('forward');
    
    if (videoRef.current) {
      console.log('📹 Pausing video and resetting to trimStart:', trimStart);
      videoRef.current.pause();
      videoRef.current.currentTime = trimStart;
    } else {
      console.log('❌ No video ref available for reset');
    }
    
    console.log('✅ Boomerang preview stopped');
  };







  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup when changing steps
  useEffect(() => {
    if (currentStep !== 2) {
      stopBoomerangPreview();
    }
  }, [currentStep]);

  // Add smoothing point
  const addSmoothingPoint = () => {
    // Use the effects video ref if in step 3, otherwise use main video ref
    const activeVideo = currentStep === 3 ? effectsVideoRef.current : videoRef.current;
    
    if (activeVideo && videoDuration > 0) {
      const currentTime = activeVideo.currentTime;
      
      // Calculate position relative to the trimmed segment
      const relativeTime = Math.max(trimStart, Math.min(trimEnd, currentTime));
      const duration = trimEnd - trimStart;
      const percentage = ((relativeTime - trimStart) / duration) * 100;
      
      console.log('Adding smoothing point:', {
        currentTime: currentTime.toFixed(2),
        relativeTime: relativeTime.toFixed(2),
        trimStart: trimStart.toFixed(2),
        trimEnd: trimEnd.toFixed(2),
        percentage: percentage.toFixed(1)
      });
      
      const newPoint = {
        id: Date.now(),
        percentage: Math.max(0, Math.min(100, percentage)),
        timePosition: relativeTime,
        absoluteTime: currentTime,
        effectType: percentage > 85 ? 'loop_transition' : 'motion_smoothing'
      };
      
      // Check if point already exists at similar position
      const existingPoint = smoothingPoints.find(point => 
        Math.abs(point.percentage - percentage) < 5
      );
      
      if (existingPoint) {
        alert(`A smoothing point already exists at ${existingPoint.percentage.toFixed(1)}%`);
        return;
      }
      
      setSmoothingPoints([...smoothingPoints, newPoint]);
      console.log('Smoothing point added successfully');
    } else {
      console.error('Cannot add smoothing point: video not ready');
      alert('Video not ready. Please wait for the video to load.');
    }
  };

  // Create boomerang with invisible loop smoothing
  const createBoomerang = async () => {
    if (!videoRef.current) return;
    
    setIsLoading(true);
    setProcessingProgress(0);
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Extract frames with higher quality from trimmed section
      const frames = [];
      const frameRate = 20; // Higher frame rate for smoother results
      const duration = trimEnd - trimStart;
      const totalFrames = Math.ceil(duration * frameRate);
      
      for (let i = 0; i < totalFrames; i++) {
        const time = trimStart + (i / frameRate);
        if (time > trimEnd) break; // Safety check
        video.currentTime = time;
        
        await new Promise(resolve => {
          video.onseeked = () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = canvas.toDataURL('image/jpeg', 0.95); // Higher quality
            frames.push(imageData);
            setProcessingProgress((i / totalFrames) * 70);
            resolve();
          };
        });
      }
      
      // Apply invisible loop smoothing if enabled
      let processedFrames = frames;
      
      if (invisibleLoopEnabled) {
        // Apply end-loop smoothing (simplified version of original algorithm)
        const endFrameCount = Math.floor(frames.length * 0.15); // Last 15% of frames
        const smoothedEndFrames = [];
        
        for (let i = 0; i < endFrameCount; i++) {
          const frameIndex = frames.length - endFrameCount + i;
          const originalFrame = frames[frameIndex];
          
          // Progressive slowdown - duplicate frames more towards the end
          const progressThroughEnd = i / endFrameCount;
          const duplicateCount = Math.floor(progressThroughEnd * 3); // 0-3 duplicates
          
          smoothedEndFrames.push(originalFrame);
          for (let j = 0; j < duplicateCount; j++) {
            smoothedEndFrames.push(originalFrame);
          }
        }
        
        // Replace the end frames with smoothed versions
        processedFrames = [
          ...frames.slice(0, frames.length - endFrameCount),
          ...smoothedEndFrames
        ];
        
        setProcessingProgress(85);
      }
      
      // Apply user smoothing points
      if (smoothingPoints.length > 0) {
        // Apply speed adjustments at smoothing points
        const enhancedFrames = [];
        
        for (let i = 0; i < processedFrames.length; i++) {
          const framePosition = i / processedFrames.length;
          let speedMultiplier = 1;
          
          // Check if this frame is affected by any smoothing points
          for (const point of smoothingPoints) {
            const pointPosition = point.percentage / 100;
            const distance = Math.abs(framePosition - pointPosition);
            
            if (distance < 0.1) { // 10% range around the point
              speedMultiplier = point.effectType === 'loop_transition' ? 0.5 : 0.7;
            }
          }
          
          enhancedFrames.push(processedFrames[i]);
          
          // Duplicate frame for slowdown effect
          if (speedMultiplier < 1) {
            const duplicates = Math.floor((1 / speedMultiplier) - 1);
            for (let j = 0; j < duplicates; j++) {
              enhancedFrames.push(processedFrames[i]);
            }
          }
        }
        
        processedFrames = enhancedFrames;
        setProcessingProgress(95);
      }
      
      // Create boomerang (forward + reverse)
      const boomerangSequence = [...processedFrames, ...processedFrames.slice(0, -1).reverse()];
      setBoomerangFrames(boomerangSequence);
      setProcessingProgress(100);
      
      // Transition to step 3 for boomerang preview
      goToStep(3);
      
      // Start auto-play boomerang after UI transition
      setTimeout(() => {
        setIsPlaying(true);
        playBoomerangFrames();
      }, 1000);
      
    } catch (error) {
      console.error('Error creating boomerang:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Play boomerang preview
  const playBoomerangPreview = () => {
    if (boomerangFrames.length === 0 || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let currentFrame = 0;
    
    const playFrame = () => {
      if (currentFrame >= boomerangFrames.length) {
        currentFrame = 0; // Loop
      }
      
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        currentFrame++;
        
        setTimeout(playFrame, 100); // 10 FPS playback
      };
      img.src = boomerangFrames[currentFrame];
    };
    
    playFrame();
  };

  // Play boomerang frames on canvas
  const playBoomerangFrames = () => {
    if (!canvasRef.current || !boomerangFrames || boomerangFrames.length === 0) {
      console.error('❌ Cannot play boomerang: missing canvas or frames');
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let frameIndex = 0;
    let direction = 1; // 1 for forward, -1 for reverse
    let localPlaying = true;

    // Set canvas size
    canvas.width = 960;
    canvas.height = 960;

    const playFrame = () => {
      if (!localPlaying || !isPlaying) {
        console.log('⏹️ Stopping boomerang playback');
        return;
      }

      const img = new Image();
      img.onload = () => {
        if (!localPlaying || !isPlaying) return;
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Update progress and direction
        const progress = (frameIndex / (boomerangFrames.length - 1)) * 100;
        setBoomerangProgress(progress);
        setBoomerangDirection(direction > 0 ? 'forward' : 'reverse');

        // Move to next frame
        frameIndex += direction;

        // Reverse direction at boundaries for boomerang effect
        if (frameIndex >= boomerangFrames.length - 1) {
          direction = -1;
          setBoomerangDirection('reverse');
        } else if (frameIndex <= 0) {
          direction = 1;
          setBoomerangDirection('forward');
        }

        // Continue playing
        setTimeout(playFrame, 83); // ~12 FPS
      };

      img.onerror = () => {
        console.error('❌ Failed to load frame', frameIndex);
        setTimeout(playFrame, 100);
      };

      img.src = boomerangFrames[frameIndex];
    };

    // Start playback
    playFrame();

    // Store stop function
    window.stopBoomerangPlayback = () => {
      localPlaying = false;
      setIsPlaying(false);
    };
  };

  // Export as GIF using canvas to blob
  const exportAsGIF = async () => {
    if (boomerangFrames.length === 0) return;
    
    try {
      setIsLoading(true);
      setProcessingProgress(0);
      
      // Create a WebM video from frames (browser native support)
      const canvas = canvasRef.current;
      const stream = canvas.captureStream(10); // 10 FPS
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        
        // Create download link
        const a = document.createElement('a');
        a.href = url;
        a.download = `boomerang-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        URL.revokeObjectURL(url);
        setIsLoading(false);
      };
      
      recorder.start();
      
      // Play through frames for recording
      let frameIndex = 0;
      const recordFrame = () => {
        if (frameIndex >= boomerangFrames.length) {
          recorder.stop();
          return;
        }
        
        const img = new Image();
        img.onload = () => {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          frameIndex++;
          setProcessingProgress((frameIndex / boomerangFrames.length) * 100);
          
          setTimeout(recordFrame, 100); // 10 FPS
        };
        img.src = boomerangFrames[frameIndex];
      };
      
      recordFrame();
      
    } catch (error) {
      console.error('Export error:', error);
      setIsLoading(false);
      alert('Export failed. Your browser may not support this feature.');
    }
  };

  // Export as MP4 (alternative format)
  const exportAsMP4 = async () => {
    // Try to use MediaRecorder with MP4 support
    try {
      if (!MediaRecorder.isTypeSupported('video/mp4')) {
        alert('MP4 export not supported in this browser. Try WebM instead.');
        return;
      }
      
      setIsLoading(true);
      const canvas = canvasRef.current;
      const stream = canvas.captureStream(15);
      const recorder = new MediaRecorder(stream, { mimeType: 'video/mp4' });
      
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `boomerang-${Date.now()}.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        URL.revokeObjectURL(url);
        setIsLoading(false);
      };
      
      recorder.start();
      setTimeout(() => recorder.stop(), 3000); // Record for 3 seconds
      
    } catch (error) {
      console.error('MP4 export error:', error);
      alert('MP4 export failed. Try WebM format instead.');
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-[920px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Admin
          </motion.button>
          <h1 className="text-2xl font-['VT323'] text-black/60">WormieEdit - Boomerang Creator</h1>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-['VT323'] text-lg ${
                currentStep === step 
                  ? 'bg-[#00FF9F] text-black' 
                  : currentStep > step 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {step}
              </div>
              <div className="ml-2 font-['VT323'] text-sm text-black/60">
                {step === 1 && 'Upload'}
                {step === 2 && 'Trim'}
                {step === 3 && 'Effects'}
                {step === 4 && 'Export'}
              </div>
              {step < 4 && (
                <div className={`w-16 h-0.5 mx-4 ${
                  currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Upload */}
      {currentStep === 1 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-8 shadow-sm border border-black/[0.08]"
        >
          <h2 className="font-['VT323'] text-xl text-black/60 mb-6">Step 1: Upload Video</h2>
          
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isUploadingVideo 
                ? 'border-[#00FF9F] bg-[#00FF9F]/5 cursor-not-allowed' 
                : 'border-gray-300 hover:border-gray-400 cursor-pointer'
            }`}
            onClick={() => !isUploadingVideo && fileInputRef.current?.click()}
          >
            {isUploadingVideo ? (
              <>
                <div className="inline-block w-12 h-12 border-2 border-[#00FF9F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="font-['VT323'] text-lg text-[#00FF9F] mb-2">
                  Loading {selectedFile?.name}...
                </p>
                <p className="font-['VT323'] text-sm text-black/60">
                  Processing video metadata
                </p>
              </>
            ) : (
              <>
                <FaUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="font-['VT323'] text-lg text-black/60 mb-2">
                  {selectedFile ? selectedFile.name : 'Click to upload video'}
                </p>
                <p className="font-['VT323'] text-sm text-black/40">
                  Supports MP4, WebM, MOV files
                </p>
              </>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            disabled={isUploadingVideo}
            className="hidden"
          />
          
          {isUploadingVideo && (
            <div className="mt-4 text-center">
              <p className="font-['VT323'] text-sm text-black/60">
                ✨ Will automatically advance to trimming step when ready
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* Step 2: Trim */}
      {currentStep === 2 && selectedFile && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-8 shadow-sm border border-black/[0.08]"
        >
          <h2 className="font-['VT323'] text-xl text-black/60 mb-6">Step 2: Trim Video</h2>
          
          {/* Video Player */}
          <div className="bg-black/5 rounded-lg p-4 mb-4 relative">
            <video
              ref={videoRef}
              className="w-full max-w-2xl mx-auto rounded-lg"
              muted
              playsInline
              loop
              onClick={togglePlayPause}
              style={{ cursor: 'pointer' }}
            />
            
            {/* Timeline Controls Overlay */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-black/80 backdrop-blur-sm rounded-lg p-4">
                                 {/* Timeline Track */}
                 <div className="relative mb-3">
                   <div
                     ref={timelineTrackRef}
                     className="w-full h-2 bg-gray-600 rounded-full cursor-pointer relative"
                     onMouseDown={startTimelineScrub}
                   >
                     {/* Selection Area */}
                     <div
                       ref={timelineSelectionRef}
                       className={`absolute top-0 h-2 bg-[#00FF9F] rounded-full transition-all duration-75 ${isDragging ? 'brightness-110 scale-y-125' : ''}`}
                       style={{ 
                         left: `${videoDuration > 0 ? (trimStart / videoDuration) * 100 : 0}%`, 
                         width: `${videoDuration > 0 ? ((trimEnd - trimStart) / videoDuration) * 100 : 100}%` 
                       }}
                     >
                       {/* Boomerang Progress Overlay */}
                       {isPreviewingTrim && (
                         <div className="absolute inset-0 overflow-hidden rounded-full">
                           {/* Progress indicator */}
                           <div
                             className={`absolute top-0 h-full rounded-full transition-all duration-75 ${
                               boomerangDirection === 'forward' 
                                 ? 'bg-gradient-to-r from-yellow-400 to-orange-500 shadow-lg' 
                                 : 'bg-gradient-to-l from-purple-400 to-pink-500 shadow-lg'
                             }`}
                             style={{ 
                               width: `${boomerangProgress}%`,
                               transform: boomerangDirection === 'reverse' ? 'scaleX(-1)' : 'scaleX(1)'
                             }}
                           />
                           
                           {/* Direction indicator */}
                           <div
                             className={`absolute -top-8 w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-sm font-bold transition-all duration-150 ${
                               boomerangDirection === 'forward' 
                                 ? 'bg-orange-500 text-white shadow-orange-500/50' 
                                 : 'bg-purple-500 text-white shadow-purple-500/50'
                             } shadow-xl animate-pulse`}
                             style={{ 
                               left: `${Math.max(-2, Math.min(92, boomerangProgress - 4))}%`
                             }}
                           >
                             {boomerangDirection === 'forward' ? '▶' : '◀'}
                           </div>
                         </div>
                       )}

                       {/* Start Handle */}
                       <div
                         ref={startHandleRef}
                         className="absolute -left-2 -top-1 w-4 h-4 bg-white border-2 border-[#00FF9F] rounded-full cursor-ew-resize shadow-lg hover:scale-110 transition-transform z-20"
                         onMouseDown={(e) => startDrag(e, 'start')}
                       />
                       {/* End Handle */}
                       <div
                         ref={endHandleRef}
                         className="absolute -right-2 -top-1 w-4 h-4 bg-white border-2 border-[#00FF9F] rounded-full cursor-ew-resize shadow-lg hover:scale-110 transition-transform z-20"
                         onMouseDown={(e) => startDrag(e, 'end')}
                       />
                     </div>
                     {/* Playhead */}
                     <div
                       ref={playheadRef}
                       className={`absolute -top-1 w-1 h-4 rounded-full shadow-lg pointer-events-none transition-colors z-15 ${
                         isPreviewingTrim ? 'bg-yellow-400 shadow-yellow-400/50' : 'bg-white'
                       }`}
                       style={{ left: '0%' }}
                     />
                   </div>
                 </div>
                
                {/* Time Display */}
                <div className="flex items-center justify-between text-sm font-['VT323'] text-white mb-3">
                  <div className="flex items-center gap-2">
                    <span>{formatTime(trimStart)}</span>
                    <span>→</span>
                    <span>{formatTime(trimEnd)}</span>
                    <span className="text-[#00FF9F]">({formatTime(trimEnd - trimStart)})</span>
                  </div>
                  <div className="text-gray-300">
                    Total: {formatTime(videoDuration)}
                  </div>
                </div>
                
                {/* Embedded Controls */}
                <div className="flex items-center justify-center gap-3">
                  {/* Boomerang Preview Button */}
                  <button
                    onClick={() => {
                      console.log('🎬 BOOMERANG BUTTON CLICKED');
                      console.log('Current state - isPreviewingTrim:', isPreviewingTrim);
                      console.log('Current state - currentStep:', currentStep);
                      console.log('Current state - videoDuration:', videoDuration);
                      console.log('Current state - trimStart:', trimStart);
                      console.log('Current state - trimEnd:', trimEnd);
                      console.log('Video ref available:', !!videoRef.current);
                      
                      if (isPreviewingTrim) {
                        console.log('🛑 Stopping boomerang preview');
                        stopBoomerangPreview();
                      } else {
                        console.log('▶️ Starting boomerang preview');
                        startBoomerangPreview();
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-['VT323'] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!videoRef.current || videoDuration === 0}
                  >
                    {isPreviewingTrim ? <FaPause /> : <FaPlay />}
                    {isPreviewingTrim ? 'Stop Preview' : 'Preview'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setTrimStart(0);
                      setTrimEnd(videoDuration);
                      if (videoRef.current) {
                        videoRef.current.currentTime = 0;
                      }
                    }}
                    className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-['VT323'] transition-colors text-sm"
                  >
                    Reset Trim
                  </button>
                </div>
              </div>
            </div>
          </div>
          


          {/* Controls */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={() => goToStep(3)}
              className="px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[16px] text-black/60 hover:text-black/80 transition-colors"
            >
              Next: Add Effects
            </button>
          </div>
          

        </motion.div>
      )}

      {/* Step 3: Effects */}
      {currentStep === 3 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-8 shadow-sm border border-black/[0.08]"
        >
          <h2 className="font-['VT323'] text-xl text-black/60 mb-6">Step 3: Boomerang Preview</h2>
          
          {/* Canvas Preview for Boomerang */}
          <div className="mb-6">
            <div className="w-full max-w-2xl mx-auto">
              <canvas
                ref={canvasRef}
                className="w-full rounded-lg shadow-lg border-2 border-purple-500 bg-gray-100"
                width="960"
                height="960"
                style={{ 
                  display: 'block',
                  minHeight: '300px',
                  backgroundColor: '#f5f5f5'
                }}
              />
              <div className="text-center mt-2 text-sm font-['VT323'] text-gray-600">
                {boomerangFrames && boomerangFrames.length > 0 ? (
                  <>Boomerang Preview • {boomerangFrames.length} frames • {isPlaying ? '🔄 Playing' : '⏸️ Stopped'}</>
                ) : (
                  <>Canvas ready for boomerang preview • Processing...</>
                )}
              </div>
            </div>
          </div>

                     {/* Playback Controls */}
           <div className="flex justify-center gap-4 mb-6">
             <button
               onClick={() => {
                 if (isPlaying) {
                   // Stop playback
                   setIsPlaying(false);
                   if (window.stopBoomerangPlayback) {
                     window.stopBoomerangPlayback();
                   }
                 } else {
                   // Start playback with boomerang frames
                   if (boomerangFrames && boomerangFrames.length > 0) {
                     setIsPlaying(true);
                     playBoomerangFrames();
                   } else {
                     alert('No boomerang frames available. Please create a boomerang first.');
                   }
                 }
               }}
               disabled={!boomerangFrames || boomerangFrames.length === 0}
               className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-['VT323'] transition-colors disabled:opacity-50"
             >
               {isPlaying ? <FaPause /> : <FaPlay />}
               {isPlaying ? 'Pause' : 'Play Boomerang'}
             </button>
           </div>
          
          {/* Boomerang Progress Indicator */}
          {isPlaying && (
            <div className="mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm font-['VT323'] text-gray-600 mb-2">
                  <span>Direction: {boomerangDirection === 'forward' ? '▶️ Forward' : '◀️ Reverse'}</span>
                  <span>Progress: {Math.round(boomerangProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-100 ${
                      boomerangDirection === 'forward' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${boomerangProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
          
          {/* Video Status */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6">
            <div className="flex items-center justify-center gap-4 text-sm font-['VT323'] text-gray-600">
              <span>🔄 Boomerang loop ready</span>
              <span>•</span>
              <span>Trimmed: {trimStart.toFixed(1)}s - {trimEnd.toFixed(1)}s</span>
            </div>
          </div>
          
          {/* Controls Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-['VT323'] text-lg text-blue-800 mb-3">✨ Enhancement Controls</h4>
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={addSmoothingPoint}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-['VT323'] transition-colors"
              >
                <FaMagic />
                Add Smoothing Point
              </button>
              
              <label className="flex items-center gap-2 font-['VT323'] text-blue-700 bg-white/50 px-3 py-2 rounded-lg border">
                <input
                  type="checkbox"
                  checked={invisibleLoopEnabled}
                  onChange={(e) => setInvisibleLoopEnabled(e.target.checked)}
                  className="w-4 h-4 accent-blue-500"
                />
                🌊 Invisible Loop Technology
              </label>
              
              {smoothingPoints.length > 0 && (
                <button
                  onClick={() => setSmoothingPoints([])}
                  className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-['VT323'] transition-colors"
                >
                  Clear All
                </button>
              )}
              
              <button
                onClick={() => {
                  console.log('=== SMOOTHING POINTS DEBUG ===');
                  console.log('Current step:', currentStep);
                  console.log('Video duration:', videoDuration);
                  console.log('Trim start:', trimStart);
                  console.log('Trim end:', trimEnd);
                  console.log('Current video time:', currentVideoTime);
                  console.log('Effects video ref:', effectsVideoRef.current);
                  console.log('Smoothing points:', smoothingPoints);
                  console.log('============================');
                }}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded font-['VT323'] text-xs transition-colors"
              >
                🐛 Debug
              </button>
            </div>
          </div>
          
          {/* Current Time Display */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="font-['VT323'] text-black/60">Current Position:</span>
              <span className="font-['VT323'] text-black/80 font-semibold">
                {videoDuration > 0 ? 
                  `${formatTime(currentVideoTime)} (${Math.round((currentVideoTime / videoDuration) * 100)}%)` : 
                  'Loading...'
                }
              </span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500 mt-1">
              <span>Trimmed section: {formatTime(trimStart)} → {formatTime(trimEnd)}</span>
              <span>Duration: {formatTime(trimEnd - trimStart)}</span>
            </div>
            {/* Show where point will be added */}
            {currentVideoTime >= trimStart && currentVideoTime <= trimEnd && (
              <div className="mt-2 text-xs text-purple-600 bg-purple-50 rounded px-2 py-1">
                ✨ Point will be added at {(((currentVideoTime - trimStart) / (trimEnd - trimStart)) * 100).toFixed(1)}% of trimmed segment
              </div>
            )}
            {(currentVideoTime < trimStart || currentVideoTime > trimEnd) && (
              <div className="mt-2 text-xs text-orange-600 bg-orange-50 rounded px-2 py-1">
                ⚠️ Current position is outside trimmed section ({formatTime(trimStart)} - {formatTime(trimEnd)})
              </div>
            )}
          </div>
          
          {/* Visual Timeline for Smoothing Points */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-['VT323'] text-lg text-gray-800 mb-3">📍 Smoothing Points Timeline</h4>
            <div className="relative">
              {/* Timeline track */}
              <div className="w-full h-3 bg-gray-200 rounded-full relative">
                {/* Current position indicator */}
                {currentVideoTime >= trimStart && currentVideoTime <= trimEnd && (
                  <div 
                    className="absolute -top-1 w-1 h-5 bg-red-500 rounded-full"
                    style={{ 
                      left: `${((currentVideoTime - trimStart) / (trimEnd - trimStart)) * 100}%`,
                      transform: 'translateX(-50%)'
                    }}
                  />
                )}
                
                {/* Smoothing points */}
                {smoothingPoints.map((point) => (
                  <div
                    key={point.id}
                    className={`absolute -top-1 w-4 h-5 rounded-full border-2 border-white shadow-lg ${
                      point.effectType === 'loop_transition' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ 
                      left: `${point.percentage}%`,
                      transform: 'translateX(-50%)'
                    }}
                    title={`${point.effectType} at ${point.percentage.toFixed(1)}%`}
                  />
                ))}
              </div>
              
              {/* Timeline labels */}
              <div className="flex justify-between text-xs font-['VT323'] text-gray-500 mt-2">
                <span>Start ({formatTime(trimStart)})</span>
                <span className="text-red-500">📍 Current Position</span>
                <span>End ({formatTime(trimEnd)})</span>
              </div>
            </div>
          </div>
          
          {/* Smoothing Points List */}
          {smoothingPoints.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-['VT323'] text-lg text-purple-800 mb-3">🎛️ Active Smoothing Points</h4>
              <div className="space-y-2">
                {smoothingPoints.map((point) => (
                  <div key={point.id} className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-['VT323'] ${
                          point.effectType === 'loop_transition' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {point.effectType === 'loop_transition' ? '🔄 Loop Transition' : '⚡ Motion Smoothing'}
                        </span>
                        <span className="font-['VT323'] text-gray-600">
                          at {point.percentage.toFixed(1)}% ({formatTime(point.timePosition)})
                        </span>
                      </div>
                      <div className="text-xs font-['VT323'] text-gray-500 mt-1">
                        Speed: {point.effectType === 'loop_transition' ? '50%' : '70%'} • 
                        Range: ±10% • 
                        Effect: {point.effectType === 'loop_transition' ? 'Smooths end-to-start transition' : 'Reduces motion speed'}
                      </div>
                    </div>
                    <button
                      onClick={() => setSmoothingPoints(points => points.filter(p => p.id !== point.id))}
                      className="text-red-500 hover:text-red-700 font-['VT323'] px-2 py-1 hover:bg-red-50 rounded transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
                     {/* Invisible Loop Info */}
           {invisibleLoopEnabled && (
             <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
               <h4 className="font-['VT323'] text-cyan-800 mb-2">🌊 Invisible Loop Technology Active</h4>
               <p className="text-sm font-['VT323'] text-cyan-700">
                 Automatic end-frame smoothing will be applied to create seamless loop transitions.
                 The last 15% of frames will be processed with progressive slowdown for natural flow.
               </p>
             </div>
           )}
           
           {/* Navigation */}
           <div className="flex justify-center gap-4 mt-6">
             <button
               onClick={() => goToStep(2)}
               className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-['VT323'] transition-colors"
             >
               Back to Trim
             </button>
             
             <button
               onClick={() => goToStep(4)}
               className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-['VT323'] transition-colors"
             >
               Export Boomerang
             </button>
           </div>
         </motion.div>
       )}

      {/* Step 4: Export */}
      {currentStep === 4 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-8 shadow-sm border border-black/[0.08]"
        >
          <h2 className="font-['VT323'] text-xl text-black/60 mb-6">Step 4: Export Boomerang</h2>
          
          {/* Results Summary */}
          {boomerangFrames.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-['VT323'] text-lg text-green-800 mb-2">✨ Boomerang Created Successfully!</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-['VT323'] text-green-700">
                <div>
                  <span className="block font-semibold">Total Frames:</span>
                  {boomerangFrames.length}
                </div>
                <div>
                  <span className="block font-semibold">Smoothing Points:</span>
                  {smoothingPoints.length}
                </div>
                <div>
                  <span className="block font-semibold">Invisible Loop:</span>
                  {invisibleLoopEnabled ? 'Enabled' : 'Disabled'}
                </div>
                <div>
                  <span className="block font-semibold">Duration:</span>
                  ~{Math.round(boomerangFrames.length / 10)}s
                </div>
              </div>
            </div>
          )}
          
          {/* Video Preview */}
          <div className="mb-6">
            <canvas
              ref={canvasRef}
              className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
              style={{ display: boomerangFrames.length > 0 ? 'block' : 'none' }}
            />
            
            {boomerangFrames.length === 0 && (
              <div className="text-center py-8">
                <p className="font-['VT323'] text-black/60">Boomerang will appear here after processing</p>
              </div>
            )}
            
            {isLoading && (
              <div className="text-center py-4">
                <div className="inline-block w-6 h-6 border-2 border-[#00FF9F] border-t-transparent rounded-full animate-spin mb-2"></div>
                <p className="font-['VT323'] text-black/60">
                  {processingProgress < 100 ? `Processing... ${processingProgress}%` : 'Exporting...'}
                </p>
              </div>
            )}
          </div>
          
          {/* Export Options */}
          <div className="space-y-4 mb-6">
            <h3 className="font-['VT323'] text-lg text-black/60">Export Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={exportAsGIF}
                disabled={isLoading || boomerangFrames.length === 0}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#00FF9F] hover:bg-[#00FF9F]/80 text-black rounded-lg font-['VT323'] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaDownload />
                Export as WebM
              </button>
              
              <button
                onClick={exportAsMP4}
                disabled={isLoading || boomerangFrames.length === 0}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-['VT323'] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaDownload />
                Export as MP4
              </button>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => goToStep(3)}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-['VT323'] transition-colors"
            >
              ← Back to Effects
            </button>
            
            <button
              onClick={() => {
                setCurrentStep(1);
                setSelectedFile(null);
                setSmoothingPoints([]);
                setBoomerangFrames([]);
                setIsUploadingVideo(false);
                setIsLoading(false);
                setProcessingProgress(0);
                setTrimStart(0);
                setTrimEnd(0);
                setVideoDuration(0);
                setIsPreviewingTrim(false);
                stopBoomerangPreview();
              }}
              className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-['VT323'] transition-colors"
            >
              🎬 New Video
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default WormieEdit; 